/**
 * SVD free-swatch mapping — peer of `svd-cascade-helper` / `svd-price-helper`, for the SVD buy-box flow only.
 *
 * Legacy PDPs source the "Order free swatches" rail by FETCHING sibling swatch products (a FABRIC add-on
 * group → `getProducts` → each product's `.swatch`). The V2 payload instead ships `swatchProducts` INLINE, so
 * the SVD flow needs no fetch — it just reshapes `SvdSwatchProduct[]` into the `ISwatch[]` the shared swatch
 * button + `onSwatchLinkClick` (cart context) + `SwatchesCartPopup` already consume, unchanged.
 *
 * Correlation seam: the shared popup pairs each swatch VARIANT to its image via a `Color` OPTION value
 * (`colorToImageMap` keyed on `options.name === 'Color'`). SVD's `SwatchVariantV2` instead references images by
 * assetId (`variant.images[]` → the parent `images` map) and carries no Color option. To reuse the popup
 * untouched, this mapping synthesizes a per-variant correlation key (the variant `id`) into BOTH the variant's
 * `options` and its image entry's `options` — a mapping-only bridge; the popup is not modified.
 */

import type { SvdSwatchProduct, SvdSwatchVariant } from '../domains/svd/svd-product.types'
import type { ISwatch, ISwatchVariant } from '../domains/Swatch'

/** The image key name the shared `SwatchesCartPopup` correlates on — an internal key, not display copy. */
// eslint-disable-next-line i18next/no-literal-string
const CORRELATION_OPTION_NAME = 'Color'

/** Minimal read view of a `MediaAsset` value (`SvdSwatchProduct.images` is loose `Record<string, unknown>`). */
interface SwatchAssetContent {
  id?: string
  url?: string
  alt?: string
  description?: string
  fileName?: string
  height?: number
  width?: number
}
const assetContent = (asset: unknown): SwatchAssetContent | undefined =>
  (asset as { media?: { content?: SwatchAssetContent } } | undefined)?.media?.content

const toISwatchVariant = (swatchProduct: SvdSwatchProduct, variant: SvdSwatchVariant): ISwatchVariant => ({
  id: variant.id,
  spid: Number(swatchProduct.spid),
  svid: variant.svid ?? 0,
  title: variant.title ?? '',
  // Synthetic correlation key (variant id) → paired with the image entry below.
  options: [{ name: CORRELATION_OPTION_NAME, value: String(variant.id) }],
})

const toISwatchImage = (swatchProduct: SvdSwatchProduct, variant: SvdSwatchVariant): ISwatch['images'][number] => {
  const content = assetContent(swatchProduct.images[variant.images?.[0] ?? ''])
  return {
    gender: '',
    // Same synthetic key as the variant, so the popup's `colorToImageMap[variant.color]` lookup resolves.
    options: [{ name: CORRELATION_OPTION_NAME, values: [String(variant.id)] }],
    image: {
      id: content?.id,
      url: content?.url ?? '',
      title: content?.alt ?? content?.fileName ?? '',
      description: content?.description,
      height: content?.height,
      width: content?.width,
    },
  }
}

/**
 * Reshape the V2 `swatchProducts` into `ISwatch[]` for the free-swatch rail. Empty/absent input → `[]`
 * (the rail button then renders nothing — the "hidden until data" state under lazy swatch hydration).
 * `tags` / `description` have no V2 equivalent → empty (the popup tolerates both).
 */
export function mapSvdSwatchProductsToISwatch(swatchProducts?: SvdSwatchProduct[]): ISwatch[] {
  return (swatchProducts ?? []).map(swatchProduct => ({
    title: swatchProduct.title ?? '',
    tags: [],
    description: '',
    spid: Number(swatchProduct.spid),
    variants: swatchProduct.variants.map(variant => toISwatchVariant(swatchProduct, variant)),
    images: swatchProduct.variants.map(variant => toISwatchImage(swatchProduct, variant)),
  }))
}
